import React, { useEffect } from "react"

import intercomImg from "@brand_assets/image/intercom_img.webp"
import { Avatar, Box, Stack, Typography } from "@mui/material"
import { useMutationIntercom } from "api/reactQuery/mutations/intercom"
import Dialog from "components/common/dialog/dialog"
import { useDialog } from "hooks/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import { selectUser } from "store/auth/auth.selectors"
import { IUserTeacher } from "ts/interfaces/User"
import { getErrorMessageFromTab } from "utils/api"

const IntercomDialog = () => {
  const userInfo = useAppSelector(selectUser)
  const { showSnackbar } = useCustomSnackbar()

  const { isOpen, handleOpen, handleClose } = useDialog()
  const userT = userInfo?.user as IUserTeacher

  useEffect(() => {
    if (userT && !userT.intercom_opened) {
      handleOpen()
    }
  }, [userT?.intercom_opened])

  const { mutate, isLoading } = useMutationIntercom({
    options: {
      onSuccess: () => {
        const storedValue = sessionStorage.getItem("specialLink")
        const isEnabledStripe = storedValue ? JSON.parse(storedValue) : null

        if (isEnabledStripe) {
          document.dispatchEvent(new CustomEvent("modalIntercomClosed"))
          sessionStorage.removeItem("specialLink")
        }
      },
      onError: (err) => {
        showSnackbar({
          variant: "error",
          title: getErrorMessageFromTab(err),
        })
      },
    },
  })

  return (
    <Dialog
      PaperProps={{
        style: {
          maxHeight: "calc(100vh - 64px)",
          height: "auto",
          display: "flex",
          flexDirection: "column",
        },
      }}
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return
        }
      }}
      disableEscapeKeyDown
      onIconClose={false}
      desktopMaxWidth="1040px"
      actionAcceptText="got it!"
      onActionButtonClick={() => {
        mutate(null)
        handleClose()
      }}
      actionAcceptButtonProps={{ disabled: isLoading }}
      contentWrapperProps={{
        sx: {
          overflow: "auto",
          maxHeight: "calc(100vh - 150px)",
          height: "auto",
        },
      }}
      scroll="paper"
    >
      <Stack
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "auto",
        }}
      >
        <Stack
          sx={{
            position: "absolute",
            left: 0,
            top: "-20px",
            flexDirection: "row",
            alignItems: "center",
            columnGap: 1,
          }}
        >
          <Avatar alt="Katie" sx={{ width: "32px", height: "32px" }}>
            K
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: "500" }}>
            Katie from ClassBank
          </Typography>
        </Stack>
        <Stack sx={{ mt: 3, rowGap: 3 }}>
          <Typography
            color="primary"
            sx={{
              textAlign: "center",
              fontSize: "32px",
              fontWeight: "600",
              lineHeight: "40px",
            }}
          >
            🥳 Welcome to ClassBank, {userT?.first_name}!
          </Typography>
          <Typography
            sx={{
              fontWeight: "500",
              maxWidth: "500px",
              width: "100%",
              mx: "auto",
              textAlign: "center",
            }}
          >
            Follow along with our{" "}
            <Typography
              color="mockup.primary_base"
              sx={{ fontWeight: "500", textDecoration: "none" }}
              component="a"
              href="https://help.classbank.com/en/collections/3184025-getting-started"
              target="_blank"
            >
              Getting Started Checklist
            </Typography>{" "}
            to get up and running in less 15 minutes!
          </Typography>
        </Stack>
        <Stack sx={{ mt: 4 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="subtitle2" style={{ fontWeight: "500" }}>
              Step 1 - Add your classes!{" "}
              <Typography component="span">Click on the + sign</Typography> on
              the left-hand side of screen
            </Typography>
          </Box>
          <img
            src={intercomImg}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "max(200px, calc(100vh - 500px))",
              minHeight: "200px",
              objectFit: "contain",
              display: "block",
            }}
            alt="screenshot"
          />
        </Stack>
      </Stack>
    </Dialog>
  )
}

export default IntercomDialog
